import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../api/client";
import { stageColor, intakeActionMeta } from "../constants/stageColors";
import { useStagesV2 } from "../lib/stagesV2";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, LabelList, LineChart, Line,
} from "recharts";
import { FileBarChart, Download, Calendar, RefreshCw, Inbox, X } from "lucide-react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MODES = [["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"], ["year", "Yearly"]];

const getStoredToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");

function todayLocalISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Count label inside a segment — only when the segment is tall enough.
function SegmentLabel({ x, y, width, height, value }) {
  if (!value || height < 16) return null;
  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#fff"
      fontSize={12}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {value}
    </text>
  );
}

export default function ActivityReports() {
  // CRM statuses v2: stage colours from GET /crm/stages when STATUS_NAMES_V2 is on.
  useStagesV2();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [view, setView] = useState("crm"); // "crm" | "intake" — client-side switch, no refetch
  const [mode, setMode] = useState("day");
  const [reportDate, setReportDate] = useState(todayLocalISO());
  const [weekday, setWeekday] = useState(""); // "" = whole week
  const [salesTeamOnly, setSalesTeamOnly] = useState(true);
  const [error, setError] = useState("");
  // Pipeline volume (BQ) — mode-independent, anchored to the date picker only
  const [volume, setVolume] = useState(null);
  const [volumeLoading, setVolumeLoading] = useState(true);
  const [volumeError, setVolumeError] = useState("");
  // Member drill-down (line chart)
  const [drill, setDrill] = useState(null); // { username, mode: "month"|"year" }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");
  const [drillMetric, setDrillMetric] = useState("total");
  // Completed Initial Visits detail (Part B) — collapsible, fetched lazily
  const [ivDetailOpen, setIvDetailOpen] = useState(false);
  const [ivDetail, setIvDetail] = useState(null);
  const [ivDetailLoading, setIvDetailLoading] = useState(false);
  const [ivDetailError, setIvDetailError] = useState("");

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({ mode, date: reportDate, sales_team_only: String(salesTeamOnly) });
    if (mode === "week" && weekday !== "") params.set("weekday", weekday);
    return params.toString();
  }, [mode, reportDate, weekday, salesTeamOnly]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/admin/activity-reports?${buildParams()}`);
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load activity report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Fetched in parallel with the main report — never blocks the chart on BQ latency.
  const fetchVolume = useCallback(async () => {
    setVolumeLoading(true);
    setVolumeError("");
    try {
      const res = await api.get(`/admin/activity-reports/volume?date=${reportDate}`);
      setVolume(res.data);
    } catch (err) {
      setVolumeError(err.response?.data?.detail || "Failed to load pipeline volume");
      setVolume(null);
    } finally {
      setVolumeLoading(false);
    }
  }, [reportDate]);

  useEffect(() => {
    fetchVolume();
  }, [fetchVolume]);

  // Completed-IV detail — only fetched while the section is expanded.
  useEffect(() => {
    if (!ivDetailOpen) return undefined;
    let cancelled = false;
    setIvDetailLoading(true);
    setIvDetailError("");
    api.get(`/admin/activity-reports/initial-visit-completions?${buildParams()}`)
      .then((res) => { if (!cancelled) setIvDetail(res.data); })
      .catch((err) => {
        if (!cancelled) setIvDetailError(err.response?.data?.detail || "Failed to load completed initial visits");
      })
      .finally(() => { if (!cancelled) setIvDetailLoading(false); });
    return () => { cancelled = true; };
  }, [ivDetailOpen, buildParams]);

  // ── Member drill-down ─────────────────────────────────────────────────
  const openDrill = useCallback(async (username) => {
    const dm = mode === "year" ? "year" : "month"; // Daily/Weekly → Monthly context
    setDrill({ username, mode: dm });
    setDrillMetric("total");
    setDrillData(null);
    setDrillError("");
    setDrillLoading(true);
    try {
      const res = await api.get(
        `/admin/activity-reports/member-trend?username=${encodeURIComponent(username)}&mode=${dm}&date=${reportDate}`
      );
      setDrillData(res.data);
    } catch (err) {
      setDrillError(err.response?.data?.detail || "Failed to load member trend");
    } finally {
      setDrillLoading(false);
    }
  }, [mode, reportDate]);

  const closeDrill = () => {
    setDrill(null);
    setDrillData(null);
    setDrillError("");
  };

  const handleBarClick = (entry) => {
    const uname = entry?.username ?? entry?.payload?.username;
    if (uname) openDrill(uname);
  };

  useEffect(() => {
    setDrillMetric("total");
  }, [view]);

  const handleExport = async () => {
    setExporting(true);
    setError("");
    try {
      const token = getStoredToken();
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${baseUrl}/api/admin/activity-reports/export?${buildParams()}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `activity_report_${mode}_${reportDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
      setError("Failed to export CSV. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const dataset = report ? report[view] : null;

  // Segment keys present in the current dataset, in canonical order.
  const segmentKeys = useMemo(() => {
    if (!dataset) return [];
    if (view === "crm") {
      const seen = new Map();
      dataset.members.forEach((m) =>
        m.stages.forEach((s) => {
          if (!seen.has(s.stage_id)) seen.set(s.stage_id, s.stage_name);
        })
      );
      return [...seen.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([id, name]) => ({ key: `s${id}`, name, color: stageColor(id) }));
    }
    // intake — backend returns member actions pre-sorted in canonical order
    const seen = new Set();
    const keys = [];
    dataset.totals.forEach((t) => {
      if (!seen.has(t.action)) {
        seen.add(t.action);
        const meta = intakeActionMeta(t.action);
        keys.push({ key: `a_${t.action}`, name: meta.label, color: meta.color });
      }
    });
    return keys;
  }, [dataset, view]);

  const chartData = useMemo(() => {
    if (!dataset) return [];
    return dataset.members.map((m) => {
      const row = { username: m.username, total: m.total };
      if (view === "crm") {
        m.stages.forEach((s) => {
          row[`s${s.stage_id}`] = s.count;
        });
      } else {
        m.actions.forEach((a) => {
          row[`a_${a.action}`] = a.count;
        });
      }
      return row;
    });
  }, [dataset, view]);

  const grandTotal = dataset ? dataset.members.reduce((sum, m) => sum + m.total, 0) : 0;
  const viewLabel = view === "crm" ? "CRM stage changes" : "intake activity";

  // Initial-visit attribution (Part B) — per-booker lookup + rows for
  // bookers with no CRM/intake actions this period (still shown honestly).
  const ivMembers = report?.initial_visits?.members || [];
  const ivUnattributed = report?.initial_visits?.unattributed || { scheduled: 0, completed: 0 };
  const ivMap = useMemo(
    () => Object.fromEntries(ivMembers.map((m) => [m.username, m])),
    [ivMembers]
  );
  const ivExtraRows = useMemo(
    () => ivMembers.filter((m) => !(dataset?.members || []).some((d) => d.username === m.username)),
    [ivMembers, dataset]
  );

  // Drill-down derived series — dropdown never refetches
  const drillRows = useMemo(() => (drillData ? drillData[view] || [] : []), [drillData, view]);

  const drillMetricOptions = useMemo(() => {
    const seen = new Map();
    drillRows.forEach((r) => {
      const id = view === "crm" ? `s${r.stage_id}` : r.action;
      const label = view === "crm" ? r.stage_name : intakeActionMeta(r.action).label;
      if (!seen.has(id)) seen.set(id, label);
    });
    return [{ id: "total", label: "Total actions" }, ...[...seen.entries()].map(([id, label]) => ({ id, label }))];
  }, [drillRows, view]);

  const drillLineData = useMemo(() => {
    if (!drillData) return [];
    const byBucket = {};
    drillRows.forEach((r) => {
      const id = view === "crm" ? `s${r.stage_id}` : r.action;
      if (drillMetric !== "total" && id !== drillMetric) return;
      byBucket[r.bucket] = (byBucket[r.bucket] || 0) + r.count;
    });
    return drillData.buckets.map((b) => ({
      bucket: b,
      label:
        drill?.mode === "month"
          ? String(parseInt(b.slice(8), 10))
          : new Date(`${b}T00:00:00`).toLocaleString("en-US", { month: "short" }),
      value: byBucket[b] || 0,
    }));
  }, [drillData, drillRows, drillMetric, drill, view]);

  const drillLineColor = useMemo(() => {
    if (drillMetric === "total") return "#2563eb";
    if (view === "crm") return stageColor(parseInt(drillMetric.slice(1), 10));
    return intakeActionMeta(drillMetric).color;
  }, [drillMetric, view]);

  const drillMetricLabel =
    drillMetricOptions.find((o) => o.id === drillMetric)?.label || "Total actions";

  return (
    <div className="flex-1 p-6 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileBarChart className="text-blue-600" size={28} />
            Reports
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Team activity{report?.period?.label ? ` — ${report.period.label}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* CRM / Intake view switch */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={() => setView("crm")}
              className={`h-9 px-4 text-sm font-medium transition-colors ${
                view === "crm" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="view-crm-btn"
            >
              CRM Activity
            </button>
            <button
              onClick={() => setView("intake")}
              className={`h-9 px-4 text-sm font-medium transition-colors ${
                view === "intake" ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="view-intake-btn"
            >
              Intake Activity
            </button>
          </div>
          {/* Daily / Weekly / Monthly / Yearly toggle */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {MODES.map(([m, label]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`h-9 px-4 text-sm font-medium transition-colors ${
                  mode === m ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
                data-testid={`mode-${m}-btn`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Date picker */}
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <input
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="report-date-input"
            />
          </div>
          {/* Day-of-week selector — weekly mode only */}
          {mode === "week" && (
            <select
              value={weekday}
              onChange={(e) => setWeekday(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="weekday-select"
            >
              <option value="">Whole week (Mon–Sun)</option>
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>{d}</option>
              ))}
            </select>
          )}
          {/* Sales team toggle */}
          <label className="flex items-center gap-2 cursor-pointer h-9 px-3 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
            <span className="text-sm text-slate-600 whitespace-nowrap">Sales Team</span>
            <button
              onClick={() => setSalesTeamOnly(!salesTeamOnly)}
              className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                salesTeamOnly ? "bg-blue-600" : "bg-slate-300"
              }`}
              data-testid="sales-team-toggle"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  salesTeamOnly ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </label>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center gap-2 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            className="h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <Download size={14} />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="report-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="animate-spin text-blue-600" size={32} />
        </div>
      ) : dataset && dataset.members.length === 0 ? (
        <div
          className="bg-white rounded-xl border border-slate-200 shadow-sm py-20 flex flex-col items-center gap-3 text-slate-500"
          data-testid="report-empty-state"
        >
          <Inbox size={40} className="text-slate-300" />
          <p className="text-sm font-medium">
            {view === "crm" ? "No activity recorded for this period" : "No intake activity recorded for this period"}
          </p>
          <p className="text-xs text-slate-400">Try a different date or switch to Weekly view.</p>
        </div>
      ) : dataset ? (
        <>
          {/* Stacked bar chart — one bar per team member */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="activity-chart">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">
                {view === "crm" ? "CRM Activity by Team Member" : "Intake Activity by Team Member"}
              </h2>
              <span className="text-sm text-slate-500" data-testid="grand-total">
                {grandTotal} {viewLabel} action{grandTotal === 1 ? "" : "s"} total · click a bar to drill down
              </span>
            </div>
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="username" tick={{ fontSize: 13, fill: "#475569" }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "rgba(148,163,184,0.08)" }}
                  formatter={(value, name) => [value, name]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {segmentKeys.map((k) => (
                  <Bar
                    key={k.key}
                    dataKey={k.key}
                    stackId="segments"
                    name={k.name}
                    fill={k.color}
                    maxBarSize={90}
                    cursor="pointer"
                    onClick={handleBarClick}
                  >
                    <LabelList dataKey={k.key} content={<SegmentLabel />} />
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Per-member breakdown table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">Breakdown — {report.period.label}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="activity-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-5 py-3 font-semibold text-slate-700">Team Member</th>
                    <th className="text-left px-5 py-3 font-semibold text-slate-700">
                      {view === "crm" ? "Stage Changes" : "Intake Actions"}
                    </th>
                    <th className="text-center px-5 py-3 font-semibold text-slate-700">Total</th>
                    <th className="text-center px-5 py-3 font-semibold text-slate-700">IV Scheduled</th>
                    <th className="text-center px-5 py-3 font-semibold text-slate-700">IV Completed (credited)</th>
                  </tr>
                </thead>
                <tbody>
                  {dataset.members.map((m) => (
                    <tr
                      key={m.username}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`member-row-${m.username}`}
                    >
                      <td className="px-5 py-3 font-medium text-slate-900">{m.username}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-2">
                          {view === "crm"
                            ? m.stages.map((s) => (
                                <span
                                  key={s.stage_id}
                                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200"
                                >
                                  <span
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: stageColor(s.stage_id) }}
                                  />
                                  {s.stage_name}
                                  <span className="font-semibold">{s.count}</span>
                                </span>
                              ))
                            : m.actions.map((a) => {
                                const meta = intakeActionMeta(a.action);
                                return (
                                  <span
                                    key={a.action}
                                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200"
                                  >
                                    <span
                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: meta.color }}
                                    />
                                    {meta.label}
                                    <span className="font-semibold">{a.count}</span>
                                  </span>
                                );
                              })}
                        </div>
                      </td>
                      <td className="text-center px-5 py-3">
                        <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">
                          {m.total}
                        </span>
                      </td>
                      <td className="text-center px-5 py-3" data-testid={`iv-scheduled-${m.username}`}>
                        {ivMap[m.username]?.scheduled ?? 0}
                      </td>
                      <td className="text-center px-5 py-3" data-testid={`iv-completed-${m.username}`}>
                        {ivMap[m.username]?.completed ?? 0}
                      </td>
                    </tr>
                  ))}
                  {/* Bookers with IV activity but no CRM/intake actions this
                      period — still shown so scheduling credit never hides. */}
                  {ivExtraRows.map((m) => (
                    <tr key={`iv-${m.username}`} className="border-b border-slate-100 bg-slate-50/50" data-testid={`iv-only-row-${m.username}`}>
                      <td className="px-5 py-3 font-medium text-slate-700">{m.username}</td>
                      <td className="px-5 py-3 text-xs text-slate-400 italic">
                        No {view === "crm" ? "stage changes" : "intake actions"} this period
                      </td>
                      <td className="text-center px-5 py-3 text-slate-400">—</td>
                      <td className="text-center px-5 py-3" data-testid={`iv-scheduled-${m.username}`}>{m.scheduled}</td>
                      <td className="text-center px-5 py-3" data-testid={`iv-completed-${m.username}`}>{m.completed}</td>
                    </tr>
                  ))}
                  {/* Honest bucket — completions/bookings with no resolvable booker. */}
                  {(ivUnattributed.scheduled > 0 || ivUnattributed.completed > 0) && (
                    <tr className="border-b border-slate-100 bg-amber-50/50" data-testid="iv-unattributed-row">
                      <td className="px-5 py-3 font-medium text-amber-700">Unattributed</td>
                      <td className="px-5 py-3 text-xs text-amber-600 italic">No booker could be resolved</td>
                      <td className="text-center px-5 py-3 text-slate-400">—</td>
                      <td className="text-center px-5 py-3">{ivUnattributed.scheduled}</td>
                      <td className="text-center px-5 py-3">{ivUnattributed.completed}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Completed Initial Visits — booker-credited detail (collapsible) */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setIvDetailOpen((o) => !o)}
              className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
              data-testid="iv-completions-toggle"
            >
              <h2 className="text-lg font-semibold text-slate-900">
                Completed Initial Visits — {report.period.label}
              </h2>
              <span className="text-sm text-slate-500">{ivDetailOpen ? "Hide" : "Show"}</span>
            </button>
            {ivDetailOpen && (
              ivDetailError ? (
                <div className="mx-5 mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="iv-completions-error">
                  {ivDetailError}
                </div>
              ) : ivDetailLoading ? (
                <div className="px-5 pb-5 flex items-center gap-2 text-slate-400 text-sm" data-testid="iv-completions-loading">
                  <RefreshCw className="animate-spin" size={14} />
                  Loading completed initial visits…
                </div>
              ) : ivDetail ? (
                <div className="overflow-x-auto border-t border-slate-100">
                  <table className="w-full text-sm" data-testid="iv-completions-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-5 py-2.5 font-semibold text-slate-700">Completed</th>
                        <th className="text-left px-5 py-2.5 font-semibold text-slate-700">Patient</th>
                        <th className="text-left px-5 py-2.5 font-semibold text-slate-700">Clinic</th>
                        <th className="text-left px-5 py-2.5 font-semibold text-slate-700">Booked By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ivDetail.completions.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-5 py-6 text-center text-slate-400 text-sm" data-testid="iv-completions-empty">
                            No completed initial visits in this period
                          </td>
                        </tr>
                      ) : (
                        ivDetail.completions.map((c, i) => (
                          <tr key={`${c.patient_name}-${i}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`iv-completion-row-${i}`}>
                            <td className="px-5 py-2.5 whitespace-nowrap">{new Date(c.completed_date).toLocaleDateString()}</td>
                            <td className="px-5 py-2.5 font-medium text-slate-900">{c.patient_name}</td>
                            <td className="px-5 py-2.5">{c.clinic || "—"}</td>
                            <td className="px-5 py-2.5">{c.booked_by}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null
            )}
          </div>
        </>
      ) : null}

      {/* Pipeline Volume — mode-independent (BQ weststar_documents) */}
      <div className="space-y-4" data-testid="pipeline-volume-section">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Pipeline Volume</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Received — referrals arriving in period · Marked Ready — referrals marked ready in period (any arrival date).
            Anchored to the selected date (Central time) — Selected Day / full Mon–Sun Week / Month-to-date / Year-to-date.
            Not affected by the mode toggle or Sales Team filters.
          </p>
        </div>

        {volumeError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="volume-error">
            {volumeError}
          </div>
        ) : volumeLoading ? (
          <div
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 flex items-center gap-2 text-slate-400 text-sm"
            data-testid="volume-loading"
          >
            <RefreshCw className="animate-spin" size={16} />
            Loading pipeline volume…
          </div>
        ) : volume ? (
          <>
            {/* By Case Type */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">Received — By Case Type</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="volume-case-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-5 py-2.5 font-semibold text-slate-700">Case Type</th>
                      <th className="text-center px-4 py-2.5 font-semibold text-slate-700">Selected Day</th>
                      <th className="text-center px-4 py-2.5 font-semibold text-slate-700">Week</th>
                      <th className="text-center px-4 py-2.5 font-semibold text-slate-700">MTD</th>
                      <th className="text-center px-4 py-2.5 font-semibold text-slate-700">YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {volume.by_case_type.map((r) => (
                      <tr
                        key={r.case_type}
                        className={
                          r.case_type === "TOTAL"
                            ? "bg-slate-100 font-semibold border-t-2 border-slate-300"
                            : "border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        }
                        data-testid={`volume-case-row-${r.case_type}`}
                      >
                        <td className="px-5 py-2.5">{r.case_type}</td>
                        <td className="text-center px-4 py-2.5">{`${r.day.count} (${r.day.inc} inc)`}</td>
                        <td className="text-center px-4 py-2.5">{`${r.week.count} (${r.week.inc} inc)`}</td>
                        <td className="text-center px-4 py-2.5">{`${r.mtd.count} (${r.mtd.inc} inc)`}</td>
                        <td className="text-center px-4 py-2.5">{`${r.ytd.count} (${r.ytd.inc} inc)`}</td>
                      </tr>
                    ))}
                    {/* Marked Ready — throughput per window (distinct
                        referrals, any arrival date); total only, no
                        case-type split in v1. */}
                    {volume.marked_ready && (
                      <tr className="bg-emerald-50/60 font-semibold border-t-2 border-emerald-200" data-testid="volume-marked-ready-row">
                        <td className="px-5 py-2.5 text-emerald-800">Marked Ready</td>
                        <td className="text-center px-4 py-2.5" data-testid="marked-ready-day">{volume.marked_ready.day}</td>
                        <td className="text-center px-4 py-2.5" data-testid="marked-ready-week">{volume.marked_ready.week}</td>
                        <td className="text-center px-4 py-2.5" data-testid="marked-ready-mtd">{volume.marked_ready.mtd}</td>
                        <td className="text-center px-4 py-2.5" data-testid="marked-ready-ytd">{volume.marked_ready.ytd}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="px-5 py-2 text-[11px] text-slate-400 border-t border-slate-100" data-testid="marked-ready-footnote">
                Marked Ready tracked from Jul 21, 2026 — earlier ready events lack a referral identifier and are excluded.
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">By Therapy Type</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="volume-therapy-table">
                  <thead>
                    <tr className="bg-slate-50">
                      <th rowSpan={2} className="text-left px-5 py-2 font-semibold text-slate-700 border-b border-slate-200">
                        Therapy
                      </th>
                      {["PI", "WC", "Unknown"].map((c) => (
                        <th
                          key={c}
                          colSpan={4}
                          className="text-center px-3 py-2 font-semibold text-slate-700 border-l border-b border-slate-200"
                        >
                          {c}
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {["PI", "WC", "Unknown"].flatMap((c) =>
                        ["Day", "Week", "MTD", "YTD"].map((w, i) => (
                          <th
                            key={`${c}-${w}`}
                            className={`text-center px-3 py-1.5 text-xs font-medium text-slate-500 ${i === 0 ? "border-l border-slate-200" : ""}`}
                          >
                            {w}
                          </th>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {volume.by_therapy_type.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="px-5 py-6 text-center text-slate-400 text-sm" data-testid="volume-therapy-empty">
                          No volume data for this period
                        </td>
                      </tr>
                    ) : (
                      volume.by_therapy_type.map((r) => (
                        <tr
                          key={r.therapy_type}
                          className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                          data-testid={`volume-therapy-row-${r.therapy_type}`}
                        >
                          <td className="px-5 py-2.5 font-medium text-slate-900">{r.therapy_type}</td>
                          {["PI", "WC", "Unknown"].flatMap((c) =>
                            ["day", "week", "mtd", "ytd"].map((w, i) => (
                              <td
                                key={`${c}-${w}`}
                                className={`text-center px-3 py-2.5 ${i === 0 ? "border-l border-slate-100" : ""}`}
                              >
                                {r[c][w]}
                              </td>
                            ))
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Member drill-down — line chart (on-screen exploration only, not in CSV) */}
      {drill && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
          data-testid="drill-modal"
        >
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  {drill.username} — {view === "crm" ? "CRM" : "Intake"} activity trend
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {drillData?.period?.label || ""} · {drill.mode === "month" ? "one point per day" : "one point per month"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={drillMetric}
                  onChange={(e) => setDrillMetric(e.target.value)}
                  className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="drill-metric-select"
                >
                  {drillMetricOptions.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
                <button
                  onClick={closeDrill}
                  className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center gap-1.5 transition-colors"
                  data-testid="drill-close-btn"
                >
                  <X size={14} />
                  Close
                </button>
              </div>
            </div>

            {drillError ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="drill-error">
                {drillError}
              </div>
            ) : drillLoading ? (
              <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2" data-testid="drill-loading">
                <RefreshCw className="animate-spin" size={16} />
                Loading trend…
              </div>
            ) : drillData ? (
              <div data-testid="drill-line-chart">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={drillLineData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#475569" }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                    <Tooltip
                      cursor={{ stroke: "#cbd5e1" }}
                      formatter={(value) => [value, drillMetricLabel]}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.bucket || label}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name={drillMetricLabel}
                      stroke={drillLineColor}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
